import { FC, ReactNode, RefObject, useLayoutEffect, useRef } from 'react'

import {
  Text,
  IconMinimize,
  IconExpand,
  IconExclamationTriangle,
  BorderRadius6,
  Button,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  IndigoIndigo50,
  Primary12Regular,
  Primary13Regular,
  Primary13SemiBold,
  RedRed60,
  ShadowM,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useReactFlow } from '@xyflow/react'
import { useAtomValue, useSetAtom } from 'jotai'
import { match } from 'ts-pattern'
import {
  collapsedNodeSizesAtom,
  expandedNodeSizesAtom,
  expandTransitionProgressAtom
} from 'Automations/automationEditor/atoms'
import {
  getNodeDescription,
  useNode,
  useNodeExpansion,
  useNodeHasValidationResults
} from 'Automations/nodeDefinitions'
import { useNodeHasConfigErrors } from '../validation/hooks/useNodeHasConfigErrors'

const lerp = (from: number, to: number, progress: number): number =>
  from + (to - from) * progress

// Vertical gap between a node's stacked sections (label, expanded content, rows).
const NODE_CONTENT_GAP = '8px'

const Container: FC<{
  nodeId: string
  children: ReactNode
  width?: string
  minWidth?: string
  selected: boolean
  expandedContent?: ReactNode
}> = ({ nodeId, children, width, minWidth, selected, expandedContent }) => {
  const hasValidationResults = useNodeHasValidationResults({ nodeId })
  const hasConfigErrors = useNodeHasConfigErrors({ nodeId })
  const { isExpanded } = useNodeExpansion({ nodeId })
  const { ref, pinnedSize, isExpanding } = useNodeExpandSize(nodeId, isExpanded)

  const expandable = expandedContent != null
  // Keep the expanded content mounted through the collapse animation (when
  // isExpanded has already flipped to false but progress hasn't reached 0) so
  // its shrink can be seen.
  const showExpandedContent = expandable && isExpanding

  return (
    <Flex
      ref={ref}
      // Mark the node as a hover group so the expand toggle can fade in only
      // while the node is hovered (see ExpandToggle's _groupHover).
      role="group"
      position="relative"
      flexDir="column"
      padding="12px"
      borderRadius="12px"
      background={GreyWhite}
      gap={NODE_CONTENT_GAP}
      width={pinnedSize ? `${pinnedSize.width}px` : width}
      height={pinnedSize ? `${pinnedSize.height}px` : undefined}
      minWidth={pinnedSize ? undefined : minWidth}
      outline={match({ selected, hasConfigErrors })
        .with(
          { selected: true, hasConfigErrors: true },
          () => `${RedRed60} 2px solid`
        )
        .with(
          { selected: true, hasConfigErrors: false },
          () => `${IndigoIndigo50} 2px solid`
        )
        .with({ selected: false }, () => `${GreyGrey30} 1px solid`)
        .exhaustive()}
      boxShadow={selected ? ShadowM : undefined}
    >
      {hasValidationResults && <ValidationErrorIcon />}
      <Label nodeId={nodeId} expandable={expandable} />
      {showExpandedContent && (
        // Clip the expanded content to the spare vertical space the pinned box
        // has beyond the label and rows, so it's revealed/hidden as the box
        // grows/shrinks. `flex="1"` (basis 0) means it never steals height from
        // the label/rows, and scoping the clip here — rather than the outer box
        // — keeps the handles, title and rows from being clipped during the
        // transition. It sits above the rows so the connector row stays pinned
        // to the bottom edge nearest its outgoing handle.
        //
        // The gap below this section is carried as a clipped spacer inside the
        // wrapper and cancelled from the column with a negative `marginBottom`,
        // so it's revealed/hidden with the content rather than springing to
        // full height the instant the wrapper mounts. Otherwise the column's
        // content would be one gap taller than the pinned (≈collapsed) box at
        // the start of the transition; with nothing left to shrink, that
        // deficit eats the bottom padding and the rows visibly jump before the
        // box catches up. (A `paddingBottom` can't be used for the spacer:
        // border-box padding sets a minimum height the wrapper can't collapse
        // below, whereas an overflow-clipped child can.)
        <Flex
          flex="1"
          minH="0"
          overflow="hidden"
          flexDir="column"
          marginBottom={`-${NODE_CONTENT_GAP}`}
        >
          {expandedContent}
          <Box minH={NODE_CONTENT_GAP} flexShrink={0} />
        </Flex>
      )}
      {children}
    </Flex>
  )
}

const ExpandToggle: FC<{ isExpanded: boolean; onToggle: () => void }> = ({
  isExpanded,
  onToggle
}) => {
  const Icon = isExpanded ? IconMinimize : IconExpand

  return (
    <Center
      marginLeft="auto"
      opacity={0}
      transition="opacity 150ms ease-in-out"
      _groupHover={{ opacity: 1 }}
    >
      <Button
        data-testid="automations.editor.node.expandToggle"
        variant="ghost"
        // Stop the click bubbling to React Flow, which would otherwise select
        // the node as a side effect of toggling its expansion.
        onClick={e => {
          e.stopPropagation()
          onToggle()
        }}
        size="32px"
        icon={<Icon color={GreyGrey80} />}
        aria-label={isExpanded ? 'Collapse' : 'Expand'}
      />
    </Center>
  )
}

type PinnedSize = { width: number; height: number }

/**
 * Drives a node's size during the expand/collapse transition.
 *
 * Captures the node's natural expanded size once (the FLIP measure-the-DOM
 * trick), then — while a transition is in flight — returns a `pinnedSize`
 * interpolated between the settled collapsed and expanded sizes by the shared
 * progress. `pinnedSize` is `undefined` at both ends of the transition (fully
 * collapsed or fully expanded), releasing the node back to content sizing.
 * `isExpanding` stays true for the whole transition (including the collapse,
 * after `isExpanded` has flipped to false) so the caller keeps the expanded
 * content mounted while it shrinks.
 */
const useNodeExpandSize = (
  nodeId: string,
  isExpanded: boolean
): {
  ref: RefObject<HTMLDivElement>
  pinnedSize: PinnedSize | undefined
  isExpanding: boolean
} => {
  const progress = useAtomValue(expandTransitionProgressAtom)
  const collapsedSize = useAtomValue(collapsedNodeSizesAtom)[nodeId]
  const expandedSizes = useAtomValue(expandedNodeSizesAtom)
  const setExpandedSizes = useSetAtom(expandedNodeSizesAtom)
  const expandedSize = expandedSizes[nodeId]
  const { getZoom } = useReactFlow()

  const ref = useRef<HTMLDivElement>(null)
  const hasMeasuredRef = useRef(false)

  // Measure the node's natural expanded size the first time it renders its
  // expanded content, before the size is pinned for the animation. Runs in a
  // layout effect (before paint) so the natural-size frame is never shown.
  useLayoutEffect(() => {
    if (!isExpanded || hasMeasuredRef.current) return
    const element = ref.current
    if (!element) return

    // getBoundingClientRect is in screen space, scaled by the canvas zoom,
    // whereas collapsedNodeSizes come from ReactFlow's store in flow space.
    // Divide by the zoom so the expanded size is captured in the same space —
    // otherwise measuring while zoomed out yields a too-small expanded size and
    // the gap-preserving layout mis-pushes the neighbouring nodes.
    const zoom = getZoom()
    const rect = element.getBoundingClientRect()
    const width = rect.width / zoom
    const height = rect.height / zoom
    hasMeasuredRef.current = true
    setExpandedSizes(prev => ({ ...prev, [nodeId]: { width, height } }))
  }, [isExpanded, nodeId, setExpandedSizes, getZoom])

  const isExpanding = isExpanded || progress > 0
  const sizesKnown = collapsedSize != null && expandedSize != null
  const isPinned = sizesKnown && isExpanding && progress < 1

  const pinnedSize: PinnedSize | undefined = isPinned
    ? {
        width: lerp(collapsedSize.width, expandedSize.width, progress),
        height: lerp(collapsedSize.height, expandedSize.height, progress)
      }
    : undefined

  return { ref, pinnedSize, isExpanding }
}

const ValidationErrorIcon: FC = () => {
  return (
    <Center
      data-testid="automations.editor.node.validationError"
      boxSize="24px"
      borderRadius="24px"
      background={RedRed60}
      position="absolute"
      top="0"
      right="0px"
      transform="translate(50%, -50%)"
      outline={`${GreyWhite} 2px solid`}
    >
      <IconExclamationTriangle color={GreyWhite} />
    </Center>
  )
}

const Label: FC<{ nodeId: string; expandable: boolean }> = ({
  nodeId,
  expandable
}) => {
  const { node, name, definition } = useNode({ nodeId })
  const { isExpanded, toggle } = useNodeExpansion({ nodeId })

  if (!node || !definition) return null

  const Icon = definition.icon
  const description = getNodeDescription(definition, node.data)

  return (
    <Flex gap="8px" alignItems="center" overflow="hidden" flexShrink={0}>
      <Icon boxSize="36px" />

      <Flex gap="4px" flexDir="column" overflow="hidden">
        <Text
          data-testid="automations.editor.canvasNode.name"
          {...Primary13SemiBold}
          color={GreyGrey80}
          overflow="hidden"
          textOverflow="ellipsis"
          wordBreak="break-all"
        >
          {name}
        </Text>
        {description && (
          <Text
            data-testid="automations.editor.canvasNode.description"
            {...Primary12Regular}
            color={GreyGrey60}
          >
            {description}
          </Text>
        )}
      </Flex>

      {expandable && <ExpandToggle isExpanded={isExpanded} onToggle={toggle} />}
    </Flex>
  )
}

const Rows: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <Flex flexDir="column" gap="4px">
      {children}
    </Flex>
  )
}

const Row: FC<{
  children: ReactNode
  onClick?: () => void
  'data-testid'?: string
}> = ({ children, onClick, 'data-testid': dataTestId }) => {
  return (
    <Flex
      data-testid={dataTestId}
      background={GreyGrey20}
      borderRadius={BorderRadius6}
      padding="8px"
      justifyContent="flex-end"
      position="relative"
      transition="background 250ms"
      {...(onClick && {
        cursor: 'pointer',
        _hover: { background: GreyGrey30 },
        onClick
      })}
    >
      {children}
    </Flex>
  )
}

const RowText: FC<{
  children: ReactNode
  strong?: boolean
  'data-testid'?: string
}> = ({ children, strong = false, 'data-testid': dataTestId }) => {
  const font = strong ? Primary13SemiBold : Primary13Regular

  return (
    <Text data-testid={dataTestId} {...font} color={GreyGrey70} noOfLines={1}>
      {children}
    </Text>
  )
}

export const EditorNodeTemplate = Object.assign(Container, {
  Rows,
  Row: Object.assign(Row, { Text: RowText })
})
